import { useEffect, useRef } from "react";
import useDesignManagement from "pages/creative/pageV2/useDesignManagement";
import { useGetFlyerInfo } from "pages/flyer/flyerTypePage/useGetFlyerInfo";
import type { DesignView } from "./types";

export const useHydrateSelectedDesign = ({
  clientId,
  selectedDesign,
  setSelectedDesign,
}: {
  clientId?: string | null;
  selectedDesign: DesignView | null;
  setSelectedDesign: (design: DesignView | null) => void;
}) => {
  const { flyerInfo } = useGetFlyerInfo();
  const { getDesign } = useDesignManagement({ clientId: clientId || "" });
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current || selectedDesign) return;
    const activeId = flyerInfo?.activeRoomId;
    if (!activeId) return;
    hydrated.current = true;
    let cancelled = false;
    getDesign(activeId)
      .then((design) => {
        if (cancelled || !design) {
          hydrated.current = false;
          return;
        }
        setSelectedDesign({
          id: design.id,
          designName: design.filename,
          format: design.format,
          thumbnailUrl: design.thumbnailUrl,
          s3Link: design.s3Link,
          addressBox: design.addressBox,
          // PAD-32920: goToBuilder only auto-imports an uploaded PDF when it knows
          // the design is uploaded and not yet edited.
          isUploaded: design.isUploaded,
          hasHtmlContent: design.hasHtmlContent,
          designSource: design.designSource,
          hasFigmaPlaceholders: design.hasFigmaPlaceholders,
          usedByCampaign: design.usedByCampaign,
        });
      })
      .catch(() => {
        hydrated.current = false;
      });
    return () => {
      cancelled = true;
    };
    // getDesign/setSelectedDesign are recreated every render; including them re-runs this
    // effect each render, and the cleanup cancels the in-flight getDesign before it resolves
    // (the design payload is large/slow) — so selectedDesign never hydrated. Depend only on
    // activeRoomId (+ the selectedDesign guard); hydrated.current prevents a double fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyerInfo?.activeRoomId, selectedDesign]);
};
